import { z } from 'zod'

/**
 * Every chart panel's id, in rail order. Its own module, apart from the registry in `panels.ts`:
 * the route's `validateSearch` reads this, and a route's search schema lands in the entry chunk,
 * which must not pull the registry's icons and lazy tab imports in with it.
 */
export const PANEL_IDS = [
  'ask',
  'glance',
  'lists',
  'screener',
  'company',
  'dividends',
  'issues',
  'performance',
  'levels',
  'flow',
  'panes',
  'alerts',
  'position',
  'notes',
  'news',
  'help',
] as const

export type PanelId = (typeof PANEL_IDS)[number]

/**
 * What the chart page shows over its chart besides the chart itself: the pinned Advanced screener
 * tab or the New tab page (features/chart-tabs). No `?view=` is the chart.
 */
export const CHART_VIEWS = ['screener', 'new'] as const

export type ChartView = (typeof CHART_VIEWS)[number]

/**
 * `/chart/$symbol`'s search. An unknown or misspelt `?panel=` or `?view=` reads as none rather than
 * throwing the route into its error page: a stale link should still open the chart.
 */
export const chartSearchSchema = z.object({
  panel: z.enum(PANEL_IDS).optional().catch(undefined),
  view: z.enum(CHART_VIEWS).optional().catch(undefined),
})

export type ChartSearch = z.infer<typeof chartSearchSchema>

export const isPanelId = (value: unknown): value is PanelId =>
  typeof value === 'string' && (PANEL_IDS as readonly string[]).includes(value)
